import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Search, ShoppingBag, MapPin, User, LogOut, ChevronDown } from "lucide-react";
import { LOGOS } from "@/data/site";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/brands", label: "Our Brands" },
  { to: "/products", label: "Shop" },
  { to: "/locations", label: "Locations" },
  { to: "/franchise", label: "Partner With Us" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const loc = useLocation();
  const { count } = useCart();
  const { user, isAuthed, isAdmin, logout } = useAuth();
  const userRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
    setUserOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const initials = user?.name
    ? user.name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()
    : "";

  const onLogout = () => {
    logout();
    setUserOpen(false);
    toast.success("Signed out.");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    toast.info("Search is coming soon — explore our shop in the meantime.");
    setSearchOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[var(--green)] text-white shadow-[0_4px_18px_-12px_rgba(0,0,0,0.35)]"
    >
      <div className="max-w-[1480px] mx-auto px-4 md:px-8 h-[78px] flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
          <img
            src={LOGOS.native_touch}
            alt="Native Touch Group"
            className="h-16 w-16 md:h-[68px] md:w-[68px] object-contain rounded-full bg-[#fdf8e6] p-0.5 shadow-sm"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-9 flex-1 justify-center">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[0.78rem] uppercase tracking-[0.18em] font-semibold relative pb-1 transition-colors ${
                  isActive
                    ? "text-[var(--gold-soft)] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:bg-[var(--gold)]"
                    : "text-white/90 hover:text-[var(--gold-soft)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Right icons */}
        <div className="flex items-center gap-1 md:gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            data-testid="nav-search-toggle"
            aria-label="Search"
            className="hidden sm:inline-flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-white/10 transition text-sm"
          >
            <Search size={18} strokeWidth={1.7} />
            <span className="kbd hidden xl:inline text-[var(--gold-soft)]">Search</span>
          </button>
          <Link
            to="/locations"
            data-testid="nav-icon-locations"
            aria-label="Find a store"
            className="hidden sm:inline-flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-white/10 transition text-sm"
          >
            <MapPin size={18} strokeWidth={1.7} />
          </Link>
          <div ref={userRef} className="relative hidden sm:block">
            {isAuthed ? (
              <>
                <button
                  type="button"
                  data-testid="nav-user-button"
                  onClick={() => setUserOpen((v) => !v)}
                  className="inline-flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white/10 transition text-sm"
                  aria-label="Account menu"
                >
                  <span className="w-7 h-7 rounded-full bg-[var(--gold)] text-[var(--green-deep)] text-[11px] font-bold flex items-center justify-center">
                    {initials || "·"}
                  </span>
                  <span className="hidden xl:inline kbd text-white/90 max-w-[120px] truncate">{user.name.split(" ")[0]}</span>
                  <ChevronDown size={14} className={`hidden xl:inline transition-transform ${userOpen ? "rotate-180" : ""}`} />
                </button>
                {userOpen && (
                  <div
                    data-testid="nav-user-menu"
                    className="absolute right-0 mt-2 w-60 bg-white text-[var(--ink)] rounded-xl shadow-xl border border-[var(--line-soft)] overflow-hidden z-50"
                  >
                    <div className="px-4 py-3 border-b border-[var(--line-soft)] bg-[var(--bg-cream)]">
                      <div className="font-serif-display text-base font-semibold leading-tight truncate">{user.name}</div>
                      <div className="text-xs text-[var(--ink-3)] truncate">{user.email}</div>
                    </div>
                    <Link to="/account/orders" data-testid="nav-my-orders" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--bg-cream)] transition">
                      <ShoppingBag size={14} className="text-[var(--gold)]" /> My Orders
                    </Link>
                    <Link to="/cart" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--bg-cream)] transition">
                      <ShoppingBag size={14} className="text-[var(--green)]" /> My Cart
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" data-testid="nav-admin-console" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--bg-cream)] transition border-t border-[var(--line-soft)] text-[var(--gold)] font-semibold">
                        <User size={14} /> Admin Console
                      </Link>
                    )}
                    <button
                      type="button"
                      data-testid="nav-logout"
                      onClick={onLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--bg-cream)] text-left transition border-t border-[var(--line-soft)]"
                    >
                      <LogOut size={14} className="text-red-600" /> Sign Out
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                aria-label="Sign in"
                className="inline-flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-white/10 transition text-sm"
              >
                <User size={18} strokeWidth={1.7} />
                <span className="kbd hidden xl:inline text-white/90">Sign In</span>
              </Link>
            )}
          </div>
          <Link
            to="/cart"
            data-testid="nav-icon-cart"
            aria-label="Cart"
            className="relative inline-flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-white/10 transition text-sm"
          >
            <ShoppingBag size={18} strokeWidth={1.7} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 bg-[var(--gold)] text-[var(--green-deep)] text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>
          <Link
            to="/franchise"
            data-testid="navbar-cta-partner"
            className="hidden lg:inline-flex btn-gold ml-2 !py-2.5 !px-4 !text-[0.7rem]"
          >
            Partner With Us
          </Link>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Inline search */}
      {searchOpen && (
        <form
          onSubmit={handleSearch}
          data-testid="nav-search-form"
          className="bg-[var(--green-dark)] border-t border-white/10"
        >
          <div className="max-w-[1480px] mx-auto px-4 md:px-8 py-3 flex gap-3">
            <input
              data-testid="nav-search-input"
              autoFocus
              placeholder="Search products, recipes, brands…"
              className="flex-1 bg-transparent border-b border-white/30 px-2 py-2 text-sm placeholder:text-white/60 focus:outline-none focus:border-[var(--gold)]"
            />
            <button type="submit" data-testid="nav-search-submit" className="btn-gold !py-2 !px-4 !text-[0.7rem]">
              Search
            </button>
          </div>
        </form>
      )}

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="navbar-mobile-drawer"
          className="lg:hidden bg-[var(--green-dark)] border-t border-white/10"
        >
          <div className="px-4 py-5 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.2em] font-semibold py-2 ${
                    isActive ? "text-[var(--gold-soft)]" : "text-white/90"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/franchise"
              data-testid="mobile-cta-partner"
              className="btn-gold mt-2"
            >
              Partner With Us
            </Link>
            {isAuthed ? (
              <button
                type="button"
                data-testid="mobile-logout"
                onClick={onLogout}
                className="mt-2 inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.2em] font-semibold py-2 text-white/90"
              >
                <LogOut size={14} /> Sign Out ({user.name.split(" ")[0]})
              </button>
            ) : (
              <Link
                to="/login"
                data-testid="mobile-login"
                className="mt-2 inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.2em] font-semibold py-2 text-[var(--gold-soft)]"
              >
                <User size={14} /> Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
